<template>
  <div class="flex items-center">
    <button
      id="dropdown-phone-button"
      data-dropdown-toggle="dropdown-phone"
      class="flex-shrink-0 z-10 inline-flex items-center py-2.5 px-4 text-sm font-medium text-center
             text-gray-900 bg-gray-100 border border-gray-300 rounded-s-lg
             hover:bg-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100
             dark:bg-gray-700 dark:hover:bg-gray-600 dark:focus:ring-gray-700
             dark:text-white dark:border-gray-600"
      type="button"
    >
      <component
        :is="currentCountry.icon"
        class="inline-block me-1"
      />
      +{{ currentCountry.code }}
      <svg
        class="w-2.5 h-2.5 ms-2.5"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 10 6"
      >
        <path
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="m1 1 4 4 4-4"
        />
      </svg>
    </button>

    <div
      id="dropdown-phone"
      class="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-52 dark:bg-gray-700 dark:divide-gray-700"
    >
      <ul
        class="py-2 text-sm text-gray-700 dark:text-gray-200"
        aria-labelledby="dropdown-phone-button"
      >
        <li
          v-for="country in countries"
          :key="country.iso"
        >
          <button
            type="button"
            class="inline-flex w-full px-4 py-2 text-sm
                   text-gray-700 hover:bg-gray-100
                   dark:text-gray-200 dark:hover:bg-gray-600
                   dark:hover:text-white"
            @click="onSelectCountry(country)"
          >
            <span class="inline-flex items-center">
              <component
                :is="country.icon"
                class="inline-block me-1"
              />
              {{ country.name }} (+{{ country.code }})
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div class="relative w-full">
      <input
        id="phone-input"
        type="text"
        class="block p-2.5 w-full z-20 text-sm text-gray-900
               bg-gray-50 rounded-e-lg border-s-0 border border-gray-300
               focus:ring-blue-500 focus:border-blue-500
               dark:bg-gray-700 dark:border-s-gray-700 dark:border-gray-600
               dark:placeholder-gray-400 dark:text-white dark:focus:border-blue-500"
        :value="phoneNumber"
        @input="onNumberInput"
        :placeholder="currentCountry.placeholder"
        required
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch, onMounted } from 'vue'
import { Dropdown } from 'flowbite';

import IconsCountriesBR from '~/components/Icons/Countries/BR.vue'
import IconsCountriesUS from '~/components/Icons/Countries/US.vue'
import IconsCountriesUK from '~/components/Icons/Countries/UK.vue'
import IconsCountriesDE from '~/components/Icons/Countries/DE.vue'
import IconsCountriesFR from '~/components/Icons/Countries/FR.vue'
import IconsCountriesES from '~/components/Icons/Countries/ES.vue'
import IconsCountriesCA from '~/components/Icons/Countries/CA.vue'
import IconsCountriesPA from '~/components/Icons/Countries/PA.vue'

import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  defaultCountryCode: {
    type: String,
    default: 'US',
  },
})

const emits = defineEmits(['update:modelValue'])

const phoneNumber = ref('')

const countries = [
  {
    iso: 'BR',
    name: t('countries.brazil'),
    code: '55',
    placeholder: 'XX XXXXX-XXXX',
    icon: IconsCountriesBR,
  },
  {
    iso: 'PA',
    name: t('countries.panama'),
    code: '507',
    placeholder: 'XXXX-XXXX',
    icon: IconsCountriesPA,
  },
  {
    iso: 'US',
    name: t('countries.united_states'),
    code: '1',
    placeholder: 'XXX XXX-XXXX',
    icon: IconsCountriesUS,
  },
  {
    iso: 'CA',
    name: t('countries.canada'),
    code: '1',
    placeholder: 'XXX XXX-XXXX',
    icon: IconsCountriesCA,
  },
  {
    iso: 'UK',
    name: t('countries.united_kingdom'),
    code: '44',
    placeholder: 'XXXX XXXXXX',
    icon: IconsCountriesUK,
  },
  {
    iso: 'DE',
    name: t('countries.germany'),
    code: '49',
    placeholder: 'XXXX XXXXXXX',
    icon: IconsCountriesDE,
  },
  {
    iso: 'FR',
    name: t('countries.france'),
    code: '33',
    placeholder: 'XX XX XX XX XX',
    icon: IconsCountriesFR,
  },
  {
    iso: 'ES',
    name: t('countries.spain'),
    code: '34',
    placeholder: 'XXX XX XX XX',
    icon: IconsCountriesES,
  },
]

const selectedCountry = ref(null)

const currentCountry = computed(() => selectedCountry.value)

const combinedValue = computed({
  get() {
    return '+' + selectedCountry.value.code + phoneNumber.value
  },
  set(newVal) {
    // If you want advanced parsing or two-way binding, do it here.
  },
})

watch([selectedCountry, phoneNumber], () => {
  emits('update:modelValue', combinedValue.value)
})

watch(
  () => props.modelValue,
  (newVal) => {
    if (!newVal) {
        selectedCountry.value = countries[0];
        phoneNumber.value = '';
        return;
    }
    let filtered = newVal.replace('+', '');
    const found = countries.find((c) => filtered.startsWith(c.code))
    if (found) {
      selectedCountry.value = found
      phoneNumber.value = filtered.replace(found.code, '') // strip out the prefix
    }
  },
  { immediate: true }
)

const onSelectCountry = (country: typeof countries[number]) => {
  selectedCountry.value = country
}

const onNumberInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  phoneNumber.value = target.value
}

onMounted(() => {
  selectedCountry.value = countries.find((c) => c.iso === props.defaultCountryCode.toUpperCase()) ?? countries[0];

  const $targetEl = document.getElementById('dropdown-phone');
  const $triggerEl = document.getElementById('dropdown-phone-button');
  new Dropdown($targetEl, $triggerEl);
})
</script>